"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Newspaper } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, type NewsFeedPost } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteSidebar } from "@/components/SiteSidebar";
import { NewsFeedCard } from "@/components/feed/NewsFeedCard";
import { NewsFeedFilters } from "@/components/feed/NewsFeedFilters";

/**
 * Twitter-style news-feed surface (feat/news-feed-twitter-style-tier3).
 *
 *   /feed/news
 *   /feed/news?topicSlug=politics
 *   /feed/news?tagSlug=#economy
 *   /feed/news?marketSlug=lula-2026-presidency
 *
 * Reads the admin-published news-hub cluster summaries (the
 * /v1/feed/news/posts endpoint backed by NewsFeedService). Each
 * post is a one-cluster summary; the card footer shows up to 3
 * source articles for attribution.
 *
 * Filter state lives on the URL via search params — single source
 * of truth, deep-linkable, no extra client state machine.
 *
 * Data flow:
 *
 *   news_hub_articles  ──┐
 *                        ├─→ news_hub_clusters
 *   news_hub_cluster_items                ↓
 *                                 ClusterSummaryPublishService
 *                                          ↓
 *                                       posts (one per cluster)
 *                                          ↓
 *                              news_hub_cluster_posts (provenance)
 *                                          ↓
 *                              GET /v1/feed/news/posts
 *                                          ↓
 *                                   NewsFeedCard render
 */
export default function NewsFeedPage() {
  return (
    <Suspense fallback={<NewsFeedFallback />}>
      <NewsFeedPageInner />
    </Suspense>
  );
}

function NewsFeedFallback() {
  return (
    <>
      <SiteHeader />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-4 py-4 md:grid-cols-[200px_1fr] md:px-5">
        <SiteSidebar />
        <main className="min-w-0 max-w-3xl">
          <p className="text-[12px] text-[var(--color-text3)]">Loading…</p>
        </main>
      </div>
    </>
  );
}

function NewsFeedPageInner() {
  const { t } = useTranslation();
  const sp = useSearchParams();
  const topicSlug = sp?.get("topicSlug") ?? null;
  const tagSlug = sp?.get("tagSlug") ?? null;
  const marketSlug = sp?.get("marketSlug") ?? null;

  // Each filter change becomes a new query key; bundling the filter
  // values into one memoized object keeps the dep list stable across
  // renders (avoids the effect refetching when query string ordering
  // shifts).
  const filterKey = useMemo(
    () => `${topicSlug ?? ""}|${tagSlug ?? ""}|${marketSlug ?? ""}`,
    [topicSlug, tagSlug, marketSlug],
  );

  const [posts, setPosts] = useState<NewsFeedPost[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);
    api
      .listNewsFeedPosts({
        topicSlug,
        tagSlug,
        marketSlug,
        limit: 20,
      })
      .then((res) => {
        if (cancelled) return;
        setPosts(res.posts);
        setNextCursor(res.nextCursor);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : t(
                "newsFeed.loadFailed",
                "News feed failed to load. Network might be flaky — refresh in a moment.",
              ),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // We intentionally use filterKey + t (the i18next singleton);
    // i18n.t is stable across renders so this fires once per filter
    // change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.listNewsFeedPosts({
        cursor: nextCursor,
        topicSlug,
        tagSlug,
        marketSlug,
        limit: 20,
      });
      setPosts((prev) => [...prev, ...res.posts]);
      setNextCursor(res.nextCursor);
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : t(
              "newsFeed.loadMoreFailed",
              "Failed to load more news posts. Retry in a moment.",
            ),
      );
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, topicSlug, tagSlug, marketSlug, t]);

  return (
    <>
      <SiteHeader />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-4 py-4 md:grid-cols-[200px_1fr] md:px-5">
        <SiteSidebar />
        <main className="min-w-0 max-w-3xl">
          <header className="mb-3">
            <h1 className="flex items-center gap-2 text-[20px] font-bold tracking-tight md:text-[22px]">
              <Newspaper
                size={18}
                strokeWidth={1.75}
                aria-hidden="true"
                className="text-[var(--color-brand)]"
              />
              {t("newsFeed.title", "News")}
            </h1>
            <p className="mt-1 text-[12px] text-[var(--color-text3)] md:text-[13px]">
              {t(
                "newsFeed.subtitle",
                "Topic, tag, and market summaries with linked sources.",
              )}
            </p>
          </header>

          <NewsFeedFilters />

          {error && (
            <p
              role="alert"
              className="mb-3 rounded-md border border-[var(--color-no)] bg-[var(--color-bg)] px-3 py-2 text-[12px]"
              style={{ color: "var(--color-no)" }}
            >
              {error}
            </p>
          )}

          {loading && posts.length === 0 && (
            <p className="text-[12px] text-[var(--color-text3)]">
              {t("newsFeed.loading", "Loading news feed…")}
            </p>
          )}

          {!loading && posts.length === 0 && !error && (
            <EmptyState
              hasFilter={Boolean(topicSlug || tagSlug || marketSlug)}
            />
          )}

          <ul className="space-y-3">
            {posts.map((post) => (
              <li key={post.id}>
                <NewsFeedCard post={post} />
              </li>
            ))}
          </ul>

          {nextCursor && (
            <button
              type="button"
              disabled={loadingMore}
              onClick={loadMore}
              className="mt-4 w-full rounded-md border border-[var(--color-border)] py-2 text-[12px] font-bold text-[var(--color-text2)] transition-colors hover:border-[var(--color-brand)] hover:text-[var(--color-brand)] disabled:opacity-50"
            >
              {loadingMore
                ? t("newsFeed.loadingMore", "Loading…")
                : t("newsFeed.loadMore", "Load more")}
            </button>
          )}
        </main>
      </div>
    </>
  );
}

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 text-center">
      <Newspaper
        size={28}
        strokeWidth={1.5}
        aria-hidden="true"
        className="mx-auto mb-2 text-[var(--color-text3)]"
      />
      <p className="text-[14px] font-bold text-[var(--color-text)]">
        {hasFilter
          ? t("newsFeed.empty.filtered", "No news matches this filter yet.")
          : t("newsFeed.empty.all", "No news yet.")}
      </p>
      <p className="mt-1 text-[12px] text-[var(--color-text3)]">
        {hasFilter ? (
          <>
            {t(
              "newsFeed.empty.tryAll",
              "Try clearing the filter to see every news item.",
            )}{" "}
            <Link
              href="/feed/news"
              className="font-bold text-[var(--color-brand)] hover:underline"
            >
              {t("newsFeed.empty.viewAll", "View all news")}
            </Link>
          </>
        ) : (
          t(
            "newsFeed.empty.checkBack",
            "The news-hub admin pipeline publishes here on a 30-minute cadence — check back shortly.",
          )
        )}
      </p>
    </div>
  );
}
